import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2, X } from 'lucide-react';
import { type ChangeEvent, type DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarInput } from '@/components/ui/CalendarInput';
import z from 'zod';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchAvailableWebinars,
  fetchEvergreenConfiguration,
  fetchWebinarById,
  updateWebinarSessionConfiguration,
  deleteWebinarSession,
  saveEvergreenSources,
} from '@/features/webinar/webinarThunks';
import { toast } from 'react-toastify';
import type { AppError } from '@/features/auth/types';
import { cn } from '@/lib/utils';
import { mapHttpError } from '@/lib/mapApiError';
import { resolveEvergreenConvertErrorKey } from '@/lib/evergreenConvertError';
import type {
  EvergreenInteractionSourceInput,
  SessionScheduleInput,
  Webinar,
  WebinarSessionConfiguration,
  WebinarSessionConfigurationInput,
} from '@/features/webinar/types';
import Loader from '@/components/ui/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { webinarService } from '@/services/webinar.service';
import { buildEvergreenPrefillTitle } from '@/components/pages/create-webinar/config/buildEvergreenPrefillTitle';
import type {
  PendingSessionCreationPayload,
  PendingSessionCreationState,
} from '@/components/pages/create-webinar/config/pendingSessionCreation';
import EvergreenMp4UploadDialog, {
  type EvergreenMp4UploadOutcome,
} from '@/components/pages/create-webinar/config/EvergreenMp4UploadDialog';
import ImportedEvergreenVideo from '@/components/pages/create-webinar/config/ImportedEvergreenVideo';
import EvergreenSourcesPanel from '@/components/pages/create-webinar/config/EvergreenSourcesPanel';
import {
  checkEvergreenMp4File,
  formatFileSize,
  inspectEvergreenMp4Video,
  type EvergreenMp4UploadError,
} from '@/lib/evergreenMp4File';
import {
  resolveLiveSourceVideoCard,
  type EvergreenConversionOutcome,
} from '@/lib/evergreenLiveSource';
import {
  buildLiveVideoSourcesInput,
  buildSourceDraft,
  findPastSession,
  findSourcesIssue,
  hasSavedEvergreenSources,
  INITIAL_EVERGREEN_SOURCES,
  interactionSourceToSave,
  findMp4ReplacementBlock,
  isInteractionsSelectionDirty,
  isSourcesSelectionDirty,
  planEvergreenSourcesSave,
  resolveEvergreenConfigurationAvailability,
  resolveInteractionsStatus,
  resolveSourceSessionLabel,
  savedSourcesKey,
  selectionFromConfiguration,
  selectionFromDraft,
  selectVideoSession,
  toSourcesEditError,
  type EvergreenSourcesSavePlan,
  type EvergreenSourcesSelection,
} from '@/lib/evergreenSources';
import { findNextUpcomingSessionDate, hasSessionInProgress } from '@/lib/evergreenMp4Replacement';
import EvergreenSourcesChangeDialog from '@/components/pages/create-webinar/config/EvergreenSourcesChangeDialog';
import { getTimezoneOptions, DEFAULT_TIMEZONE } from '@/lib/timezones';

const DUPLICATE_SESSIONS_CODE = 'DUPLICATE_SESSIONS';
const INTERACTIONS_IMPORT_POLL_INTERVAL_MS = 5_000;
// Au-delà, un import qui ne progresse plus ne justifie pas d'interroger le back indéfiniment.
const INTERACTIONS_IMPORT_POLL_MAX_MS = 30 * 60_000;

function sessionToUtcIso(
  date: string | undefined,
  hour: string,
  minute: string,
  timezone: string,
): string | null {
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  const hourNum = parseInt(hour, 10);
  const minuteNum = parseInt(minute, 10);
  const dateTimeString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hourNum).padStart(2, '0')}:${String(minuteNum).padStart(2, '0')}:00`;
  const dateTime = DateTime.fromISO(dateTimeString, { zone: timezone });
  if (!dateTime.isValid) return null;
  return dateTime.toUTC().toISO();
}

type SessionDateDetails = {
  date?: string;
  hour: string;
  minute: string;
  timezone: string;
  sessionId?: number;
  status?: string;
  deletable?: boolean;
};

type FormValues = {
  type: 'LIVE' | 'EVERGREEN';
  sessions: SessionDateDetails[];
  isEdit?: boolean;
};

function parseUtcIsoKey(dateISO: string): string | null {
  const dateTimeUTC = DateTime.fromISO(dateISO, { zone: 'utc' });
  return dateTimeUTC.isValid ? dateTimeUTC.toUTC().toISO() : null;
}

function buildSessionDetailsFromConfiguration(
  config: WebinarSessionConfiguration,
  timezone: string,
): SessionDateDetails[] {
  const metadataByUtc = new Map<
    string,
    Pick<SessionDateDetails, 'sessionId' | 'status' | 'deletable' | 'timezone'>
  >();

  for (const session of config.sessions ?? []) {
    const utcKey = parseUtcIsoKey(session.date);
    if (!utcKey) continue;
    metadataByUtc.set(utcKey, {
      sessionId: session.id,
      status: session.status,
      deletable: session.deletable,
      timezone: session.timezone || timezone,
    });
  }

  const dateIsos: string[] = [];
  const seenUtc = new Set<string>();

  const addDateIso = (dateISO: string) => {
    const utcKey = parseUtcIsoKey(dateISO);
    if (!utcKey || seenUtc.has(utcKey)) return;
    seenUtc.add(utcKey);
    dateIsos.push(dateISO);
  };

  for (const dateISO of config.nextDates ?? []) {
    addDateIso(dateISO);
  }

  for (const session of config.sessions ?? []) {
    addDateIso(session.date);
  }

  const sessionsDetails = dateIsos
    .map((dateISO): SessionDateDetails | null => {
      const utcKey = parseUtcIsoKey(dateISO);
      if (!utcKey) return null;
      const dateTimeUTC = DateTime.fromISO(dateISO, { zone: 'utc' });
      if (!dateTimeUTC.isValid) return null;
      const metadata = metadataByUtc.get(utcKey);
      const sessionTimezone = metadata?.timezone || timezone;
      const dateTimeLocal = dateTimeUTC.setZone(sessionTimezone);
      return {
        date: dateTimeLocal.toFormat('yyyy-MM-dd'),
        hour: dateTimeLocal.toFormat('HH'),
        minute: dateTimeLocal.toFormat('mm'),
        timezone: sessionTimezone,
        ...metadata,
      };
    })
    .filter((session): session is SessionDateDetails => session != null);

  sessionsDetails.sort((a, b) => {
    const aIso = sessionToUtcIso(a.date, a.hour, a.minute, a.timezone || timezone);
    const bIso = sessionToUtcIso(b.date, b.hour, b.minute, b.timezone || timezone);
    if (!aIso || !bIso) return 0;
    return DateTime.fromISO(aIso).toMillis() - DateTime.fromISO(bIso).toMillis();
  });

  return sessionsDetails;
}

interface WebinarSessionFormProps {
  webinar?: Webinar | null;
  onSuccess?: () => void;
  isEdit?: boolean;
  onSessionCreationValidated?: (payload: PendingSessionCreationPayload) => void;
  pendingSessionDraft?: PendingSessionCreationState | null;
  onSessionsChanged?: (options?: {
    excludedSessionId?: number;
    realignWithNextSession?: boolean;
  }) => void | Promise<void>;
}

export default function WebinarSessionForm({
  webinar,
  onSuccess,
  isEdit,
  onSessionCreationValidated,
  pendingSessionDraft,
  onSessionsChanged,
}: WebinarSessionFormProps) {
  const { t, i18n } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { webinarId: webinarIdParam } = useParams<{
    webinarId?: string;
  }>();
  const routeWebinarId = webinarIdParam != null ? Number.parseInt(webinarIdParam, 10) : undefined;
  const activeWebinarId =
    routeWebinarId != null && !Number.isNaN(routeWebinarId) ? routeWebinarId : webinar?.id;
  const dispatch = useAppDispatch();
  const currentSessionConfiguration = useAppSelector(
    (state) => state.webinar.currentSessionConfiguration,
  );
  const sessionConfigurationWebinarId = useAppSelector(
    (state) => state.webinar.sessionConfigurationWebinarId,
  );
  const sessionConfigurationLoading = useAppSelector(
    (state) => state.webinar.sessionConfigurationLoading,
  );
  const isSessionsLoading =
    activeWebinarId != null &&
    !(onSessionCreationValidated && !webinar && pendingSessionDraft) &&
    (sessionConfigurationLoading || sessionConfigurationWebinarId !== activeWebinarId);
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const workspaceTimezone = useAppSelector((state) => state.workspace.currentWorkspace?.timezone);
  const availableWebinars = useAppSelector((state) => state.webinar.availableWebinars ?? []);

  const availableWebinarsLoading = useAppSelector(
    (state) => state.webinar.availableWebinarsLoading ?? false,
  );

  const { currentWebinarSession } = useAppSelector((state) => state.webinar);

  const schema = useMemo(() => {
    const sessionDateSchema = z.object({
      date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, { message: t('sessionForm.validation.dateFormatInvalid') })
        .optional(),
      hour: z
        .string()
        .regex(/^\d{1,2}$/, { message: t('sessionForm.validation.hourInvalid') })
        .refine(
          (val) => {
            const num = parseInt(val, 10);
            return !Number.isNaN(num) && num >= 0 && num <= 23;
          },
          { message: t('sessionForm.validation.hourRange') },
        ),
      minute: z
        .string()
        .regex(/^\d{1,2}$/, { message: t('sessionForm.validation.minuteInvalid') })
        .refine(
          (val) => {
            const num = parseInt(val, 10);
            return !Number.isNaN(num) && num >= 0 && num <= 59;
          },
          { message: t('sessionForm.validation.minuteRange') },
        ),
      timezone: z.string().min(1, { message: t('sessionForm.validation.timezoneRequired') }),
    });

    return z
      .object({
        type: z.enum(['LIVE', 'EVERGREEN'], {
          message: t('sessionForm.validation.typeRequired'),
        }),
        sessions: z
          .array(sessionDateSchema)
          .min(1, { message: t('sessionForm.validation.dateRequired') }),
      })
      .superRefine((data, ctx) => {
        const tz = workspaceTimezone || DEFAULT_TIMEZONE;
        const isoKeys = data.sessions.map((s) =>
          sessionToUtcIso(s.date, s.hour, s.minute, s.timezone || tz),
        );
        const seen = new Set<string>();
        for (const iso of isoKeys) {
          if (iso == null) continue;
          if (seen.has(iso)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: DUPLICATE_SESSIONS_CODE,
              path: ['sessions'],
            });
            return;
          }
          seen.add(iso);
        }
      });
  }, [t, workspaceTimezone]);

  const defaultTomorrow = DateTime.now()
    .setZone(workspaceTimezone || DEFAULT_TIMEZONE)
    .plus({ days: 1 })
    .toFormat('yyyy-MM-dd');

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      type: 'LIVE',
      sessions: [
        {
          date: defaultTomorrow,
          hour: '20',
          minute: '00',
          timezone: workspaceTimezone || DEFAULT_TIMEZONE,
        },
      ],
    },
    mode: 'onSubmit',
  });

  const {
    fields: sessionFields,
    append: appendSession,
    remove: removeSession,
  } = useFieldArray({
    control: form.control,
    name: 'sessions',
  });

  const { isSubmitting } = form.formState;

  const [sources, setSources] = useState<EvergreenSourcesSelection>(INITIAL_EVERGREEN_SOURCES);
  const [showSourcesIssue, setShowSourcesIssue] = useState(false);
  const [mp4File, setMp4File] = useState<File | null>(null);
  const [mp4FileError, setMp4FileError] = useState<EvergreenMp4UploadError | null>(null);
  const [isCheckingMp4File, setIsCheckingMp4File] = useState(false);
  const [isDraggingMp4, setIsDraggingMp4] = useState(false);
  const [pendingMp4Upload, setPendingMp4Upload] = useState<{
    webinarId: number;
    file: File;
    waitForConversion: boolean;
    interactionSource: EvergreenInteractionSourceInput | null;
  } | null>(null);
  const mp4InputRef = useRef<HTMLInputElement>(null);
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);
  const hydratedWebinarIdRef = useRef<number | null>(null);

  const currentEvergreenConfiguration = useAppSelector(
    (state) => state.webinar.currentEvergreenConfiguration,
  );
  const maxSessionDurationMinutes = useAppSelector(
    (state) => state.billing.usage.data?.sessionDuration?.max ?? null,
  );
  const isConfiguredEvergreen = currentSessionConfiguration?.type === 'EVERGREEN';
  const savedEvergreenConfiguration =
    isConfiguredEvergreen && currentEvergreenConfiguration?.webinarId === activeWebinarId
      ? currentEvergreenConfiguration
      : null;
  const importedMp4VideoName = savedEvergreenConfiguration?.videoName ?? null;
  // Un Live YouTube retenu avant la séparation vidéo/interactions attend encore son MP4.
  const awaitingYoutubeMp4 = savedEvergreenConfiguration?.awaitingImportedMp4 === true;
  // Une vidéo enregistrée : les sources se modifient (publié compris), sauf pendant une session.
  const existingEvergreen = hasSavedEvergreenSources(savedEvergreenConfiguration)
    ? savedEvergreenConfiguration
    : null;
  const sessionInProgress = hasSessionInProgress(currentSessionConfiguration?.sessions);
  const sourcesLocked = existingEvergreen != null && sessionInProgress;
  const sourcesPlan = existingEvergreen
    ? planEvergreenSourcesSave(sources, existingEvergreen)
    : null;
  const sourcesDirty =
    existingEvergreen != null && isSourcesSelectionDirty(sources, existingEvergreen);
  const interactionsStatus = resolveInteractionsStatus(savedEvergreenConfiguration);
  const mp4ReplacementBlock = findMp4ReplacementBlock({
    interactionsDirty:
      existingEvergreen != null && isInteractionsSelectionDirty(sources, existingEvergreen),
    interactionsStatus,
  });
  const mp4ReplacementBlockedReason =
    mp4ReplacementBlock === 'INTERACTIONS_IMPORTING'
      ? t('sessionForm.sources.importBlocksMp4')
      : mp4ReplacementBlock === 'INTERACTIONS_DIRTY'
        ? t('sessionForm.sources.dirtyBlocksMp4')
        : null;
  const evergreenConfigurationLoading = useAppSelector(
    (state) => state.webinar.evergreenConfigurationLoading,
  );
  const evergreenConfigurationError = useAppSelector(
    (state) => state.webinar.evergreenConfigurationError,
  );
  const configurationAvailability = resolveEvergreenConfigurationAvailability({
    isConfiguredEvergreen,
    configurationLoaded: savedEvergreenConfiguration != null,
    loadFailed: !evergreenConfigurationLoading && evergreenConfigurationError != null,
  });
  const configurationPending =
    configurationAvailability === 'LOADING' || configurationAvailability === 'UNAVAILABLE';
  const savedLiveVideoSessionId =
    existingEvergreen && selectionFromConfiguration(existingEvergreen).video === 'LIVE'
      ? (existingEvergreen.videoSourceSession?.id ?? null)
      : null;
  const liveSourceVideoCard = resolveLiveSourceVideoCard({
    evergreenConfiguration:
      savedEvergreenConfiguration &&
      sources.video === 'LIVE' &&
      (savedLiveVideoSessionId == null || sources.videoSessionId === savedLiveVideoSessionId)
        ? savedEvergreenConfiguration
        : null,
    mp4Required: awaitingYoutubeMp4,
  });
  const sourcesIssue =
    form.watch('type') !== 'EVERGREEN' || sourcesLocked
      ? null
      : sourcesPlan
        ? sourcesPlan.kind === 'issue'
          ? sourcesPlan.issue
          : null
        : findSourcesIssue(sources, !!mp4File || !!importedMp4VideoName);
  const [sourcesConfirmation, setSourcesConfirmation] = useState<{
    effect: 'IMPORT_LIVE' | 'RESET';
    sourceTitle: string | null;
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  // Réaligné seulement quand les sources enregistrées changent : un rechargement de la
  // configuration (import en cours, barre latérale) ne doit pas effacer un choix en cours.
  const sourcesKeyToHydrate = savedEvergreenConfiguration
    ? savedSourcesKey(savedEvergreenConfiguration)
    : '';
  const [hydratedSourcesKey, setHydratedSourcesKey] = useState('');
  if (sourcesKeyToHydrate !== hydratedSourcesKey) {
    setHydratedSourcesKey(sourcesKeyToHydrate);
    if (savedEvergreenConfiguration) {
      setSources(selectionFromConfiguration(savedEvergreenConfiguration));
    }
  }

  // Lue par l'hydratation du formulaire sans la relancer à chaque rechargement de la configuration.
  const savedEvergreenConfigurationRef = useRef(savedEvergreenConfiguration);
  useEffect(() => {
    savedEvergreenConfigurationRef.current = savedEvergreenConfiguration;
  }, [savedEvergreenConfiguration]);

  useEffect(() => {
    if (!isConfiguredEvergreen) return;
    if (workspaceId == null || activeWebinarId == null) return;
    void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId: activeWebinarId }));
  }, [dispatch, isConfiguredEvergreen, workspaceId, activeWebinarId]);

  useEffect(() => {
    if (interactionsStatus !== 'importing') return;
    if (workspaceId == null || activeWebinarId == null) return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - startedAt > INTERACTIONS_IMPORT_POLL_MAX_MS) {
        window.clearInterval(timer);
        return;
      }
      void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId: activeWebinarId }));
    }, INTERACTIONS_IMPORT_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [dispatch, interactionsStatus, workspaceId, activeWebinarId]);

  const selectMp4File = async (file: File) => {
    setMp4File(null);
    const basicError = checkEvergreenMp4File(file);
    setMp4FileError(basicError);
    if (basicError) return;
    setIsCheckingMp4File(true);
    try {
      const videoError = await inspectEvergreenMp4Video(
        file,
        maxSessionDurationMinutes ? maxSessionDurationMinutes * 60 : null,
      );
      setMp4FileError(videoError);
      if (!videoError) setMp4File(file);
    } finally {
      setIsCheckingMp4File(false);
    }
  };

  const handleMp4InputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void selectMp4File(file);
  };

  const handleMp4Drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingMp4(false);
    const file = event.dataTransfer.files?.[0];
    if (file && !isCheckingMp4File) void selectMp4File(file);
  };

  const clearMp4File = () => {
    setMp4File(null);
    setMp4FileError(null);
  };

  useEffect(() => {
    if (workspaceId != null) {
      void dispatch(fetchAvailableWebinars({ workspaceId }));
    }
  }, [dispatch, workspaceId]);

  useEffect(() => {
    if (onSessionCreationValidated && !webinar && pendingSessionDraft) {
      return;
    }

    if (activeWebinarId == null) return;
    if (sessionConfigurationWebinarId !== activeWebinarId) return;
    if (!currentSessionConfiguration) return;
    if (hydratedWebinarIdRef.current === activeWebinarId) return;
    hydratedWebinarIdRef.current = activeWebinarId;

    const timezone = workspaceTimezone || DEFAULT_TIMEZONE;
    const sessionsDetails = buildSessionDetailsFromConfiguration(
      currentSessionConfiguration,
      timezone,
    );
    const tomorrow = DateTime.now().setZone(timezone).plus({ days: 1 }).toFormat('yyyy-MM-dd');

    form.reset({
      type: currentSessionConfiguration.type ?? 'LIVE',
      sessions:
        sessionsDetails.length > 0
          ? sessionsDetails
          : [
              {
                date: tomorrow,
                hour: '11',
                minute: '00',
                timezone,
              },
            ],
    });

    // Affichage provisoire : la configuration Evergreen précise ensuite les deux sources. Déjà
    // chargée (rechargement après un enregistrement), elle fait foi.
    const savedConfiguration = savedEvergreenConfigurationRef.current;
    if (savedConfiguration) {
      setSources(selectionFromConfiguration(savedConfiguration));
    } else if (
      currentSessionConfiguration.type === 'EVERGREEN' &&
      currentSessionConfiguration.evergreenSession
    ) {
      setSources(
        selectVideoSession(
          INITIAL_EVERGREEN_SOURCES,
          currentSessionConfiguration.evergreenSession.sessionId,
        ),
      );
    } else if (currentSessionConfiguration.type === 'EVERGREEN') {
      setSources({ ...INITIAL_EVERGREEN_SOURCES, video: 'MP4' });
    } else {
      setSources(INITIAL_EVERGREEN_SOURCES);
    }
  }, [
    activeWebinarId,
    currentSessionConfiguration,
    sessionConfigurationWebinarId,
    form,
    onSessionCreationValidated,
    webinar,
    pendingSessionDraft,
    workspaceTimezone,
  ]);

  useEffect(() => {
    if (!onSessionCreationValidated || webinar || !pendingSessionDraft) {
      return;
    }

    const fallbackTimezone = workspaceTimezone || DEFAULT_TIMEZONE;
    const sessions: SessionDateDetails[] = [];
    for (const schedule of pendingSessionDraft.sessions) {
      const dateTimeUTC = DateTime.fromISO(schedule.date, { zone: 'utc' });
      if (dateTimeUTC.isValid) {
        const sessionTimezone = schedule.timezone || fallbackTimezone;
        const dateTimeLocal = dateTimeUTC.setZone(sessionTimezone);
        sessions.push({
          date: dateTimeLocal.toFormat('yyyy-MM-dd'),
          hour: dateTimeLocal.toFormat('HH'),
          minute: dateTimeLocal.toFormat('mm'),
          timezone: sessionTimezone,
        });
      }
    }

    if (sessions.length === 0) {
      return;
    }

    form.reset({
      type: pendingSessionDraft.type,
      sessions,
    });

    const source = pendingSessionDraft.type === 'EVERGREEN' ? pendingSessionDraft.source : null;
    setSources(source ? selectionFromDraft(source) : INITIAL_EVERGREEN_SOURCES);
    setMp4File(source?.video.kind === 'MP4' ? source.video.file : null);
  }, [pendingSessionDraft, onSessionCreationValidated, webinar, workspaceTimezone, form]);

  const isDatePast = (
    date: string | undefined,
    hour: string,
    minute: string,
    timezone?: string,
  ): boolean => {
    if (!date) return false;

    try {
      const tz = timezone || workspaceTimezone || DEFAULT_TIMEZONE;
      const dateTime = DateTime.fromISO(`${date}T${hour || '00'}:${minute || '00'}:00`, {
        zone: tz,
      });

      if (!dateTime.isValid) return false;

      const now = DateTime.now().setZone(tz);
      return dateTime < now;
    } catch {
      return false;
    }
  };

  const isRowTheActualNextSession = (session: SessionDateDetails | undefined): boolean => {
    if (!isEdit || !currentWebinarSession?.id || !session) return false;
    return (
      session.sessionId != null && Number(session.sessionId) === Number(currentWebinarSession.id)
    );
  };

  const onSubmit = async (data: FormValues) => {
    const scheduleEntries = data.sessions
      .map((session) => {
        const timezone = session.timezone || workspaceTimezone || DEFAULT_TIMEZONE;
        const date = sessionToUtcIso(session.date, session.hour, session.minute, timezone);
        return date ? { date, timezone, id: session.sessionId ?? null } : null;
      })
      .filter(
        (entry): entry is { date: string; timezone: string; id: number | null } => entry !== null,
      );

    const allDatesISO = scheduleEntries.map((entry) => entry.date);
    if (new Set(allDatesISO).size !== allDatesISO.length) {
      toast.error(t('sessionForm.duplicateSessionsDialog.description'));
      return;
    }

    const now = DateTime.now().toUTC();
    const futureEntries = scheduleEntries
      .filter((entry) => {
        const dateTime = DateTime.fromISO(entry.date, { zone: 'utc' });
        return dateTime.isValid && dateTime > now;
      })
      .sort(
        (a, b) =>
          DateTime.fromISO(a.date, { zone: 'utc' }).toMillis() -
          DateTime.fromISO(b.date, { zone: 'utc' }).toMillis(),
      );

    const futureDates = futureEntries.map((entry) => entry.date);
    const futureSchedules: SessionScheduleInput[] = futureEntries.map((entry) => ({
      ...(entry.id != null ? { id: entry.id } : {}),
      date: entry.date,
      timezone: entry.timezone,
    }));

    if (futureEntries.length === 0) {
      toast.error(t('sessionForm.validation.dateRequired'));
      return;
    }

    const payload: WebinarSessionConfigurationInput = {
      type: data.type,
      sessions: futureSchedules,
    };

    if (!webinar?.id && !isEdit && onSessionCreationValidated) {
      if (!workspaceId) {
        toast.error(tCommon('errors.generic', 'Workspace introuvable.'));
        return;
      }
      if (data.type === 'EVERGREEN') {
        if (sourcesIssue) {
          setShowSourcesIssue(true);
          toast.error(t(`sessionForm.sources.errors.${sourcesIssue}`));
          return;
        }
        const source = buildSourceDraft(sources, mp4File);
        if (!source) return;
        // Titre prérempli depuis le Live de la vidéo, à défaut celui des interactions.
        const prefillWebinarId = findPastSession(
          availableWebinars,
          sources.video === 'LIVE' ? sources.videoSessionId : sources.interactionsSessionId,
        )?.webinar.id;
        let prefill: { title: string; subtitle: string; description: string } | null = null;
        if (prefillWebinarId != null) {
          try {
            const sourceWebinar = await webinarService.getById(workspaceId, prefillWebinarId);
            prefill = {
              title: buildEvergreenPrefillTitle(sourceWebinar.title ?? ''),
              subtitle: sourceWebinar.shortDescription ?? '',
              description: sourceWebinar.description ?? '',
            };
          } catch {
            toast.error(tCommon('errors.generic', 'Impossible de charger le webinaire source.'));
            return;
          }
        }
        onSessionCreationValidated({
          type: 'EVERGREEN',
          nextDates: futureDates,
          sessions: futureSchedules,
          source,
          prefill,
        });
        return;
      }

      onSessionCreationValidated({
        type: 'LIVE',
        nextDates: futureDates,
        sessions: futureSchedules,
      });
      return;
    }

    if (!workspaceId || !webinar?.id) {
      toast.error(tCommon('errors.generic', 'Webinar ou workspace introuvable.'));
      return;
    }

    const webinarId = webinar.id;

    // Sources provisoires tant que la configuration n'est pas chargée : rien ne part.
    if (data.type === 'EVERGREEN' && configurationPending) {
      toast.error(t(`sessionForm.sources.configuration.${configurationAvailability}`));
      return;
    }

    if (data.type === 'EVERGREEN' && sourcesPlan) {
      if (sourcesPlan.kind === 'issue' && !sourcesLocked) {
        setShowSourcesIssue(true);
        toast.error(t(`sessionForm.sources.errors.${sourcesPlan.issue}`));
        return;
      }
      if (sourcesPlan.kind === 'save') {
        await saveSourcesChange(workspaceId, webinarId, sourcesPlan, payload);
        return;
      }
      await saveSessionConfiguration(workspaceId, webinarId, payload);
      return;
    }

    if (data.type === 'EVERGREEN' && !existingEvergreen) {
      if (sourcesIssue) {
        setShowSourcesIssue(true);
        toast.error(t(`sessionForm.sources.errors.${sourcesIssue}`));
        return;
      }
      if (sources.video === 'MP4') {
        if (!mp4File) {
          toast.error(t('sessionForm.mp4.fileRequired'));
          return;
        }
        const interactionSource = interactionSourceToSave(sources, savedEvergreenConfiguration);
        if (await saveSessionConfiguration(workspaceId, webinarId, payload)) {
          setPendingMp4Upload({
            webinarId,
            file: mp4File,
            waitForConversion:
              awaitingYoutubeMp4 || interactionSource?.interactionSourceType === 'LIVE',
            interactionSource,
          });
        }
        return;
      }
      const input = buildLiveVideoSourcesInput(sources);
      if (!input) return;
      let conversion: EvergreenConversionOutcome | null;
      try {
        conversion = await dispatch(
          saveEvergreenSources({
            workspaceId,
            webinarId,
            input,
            awaitConversion: sources.interactions === 'LIVE',
          }),
        ).unwrap();
      } catch (err) {
        toast.error(
          tCommon(
            resolveEvergreenConvertErrorKey(err),
            'Erreur lors de la conversion en evergreen.',
          ),
        );
        return;
      }
      const saved = await saveSessionConfiguration(workspaceId, webinarId, payload);
      if (saved && conversion?.kind === 'blocked') {
        navigate(`/create-webinar/evergreen/${webinarId}`);
      } else if (saved && conversion?.kind === 'pending') {
        toast.info(t('sessionForm.mp4.conversionPending'));
      }
      return;
    }

    await saveSessionConfiguration(workspaceId, webinarId, payload);
  };

  const askInteractionsConfirmation = (
    plan: Extract<EvergreenSourcesSavePlan, { kind: 'save' }>,
  ): Promise<boolean> => {
    // Remplacer ou effacer les interactions supprime aussi leurs personnalisations.
    const effect = plan.interactionsEffect;
    if (effect === 'KEEP') return Promise.resolve(true);
    const sourceTitle =
      plan.interactionsSessionId == null
        ? null
        : resolveSourceSessionLabel(availableWebinars, plan.interactionsSessionId, [
            existingEvergreen?.interactionSourceSession,
            existingEvergreen?.videoSourceSession,
          ]);
    return new Promise((resolve) => setSourcesConfirmation({ effect, sourceTitle, resolve }));
  };

  const closeSourcesConfirmation = (confirmed: boolean) => {
    sourcesConfirmation?.resolve(confirmed);
    setSourcesConfirmation(null);
  };

  /** Modification des sources d'un Evergreen configuré, puis enregistrement des dates. */
  const saveSourcesChange = async (
    targetWorkspaceId: number,
    targetWebinarId: number,
    plan: Extract<EvergreenSourcesSavePlan, { kind: 'save' }>,
    payload: WebinarSessionConfigurationInput,
  ) => {
    if (sourcesLocked) {
      toast.error(t('sessionForm.sources.saveErrors.EVERGREEN_SESSION_IN_PROGRESS'));
      return;
    }
    if (!(await askInteractionsConfirmation(plan))) return;
    // Pas d'attente de l'import : les dates s'enregistrent tout de suite et le panneau suit
    // l'import des interactions en interrogeant la configuration.
    try {
      await dispatch(
        saveEvergreenSources({
          workspaceId: targetWorkspaceId,
          webinarId: targetWebinarId,
          input: plan.input,
        }),
      ).unwrap();
    } catch (error) {
      toast.error(t(`sessionForm.sources.saveErrors.${toSourcesEditError(error)}`));
      return;
    }
    const saved = await saveSessionConfiguration(targetWorkspaceId, targetWebinarId, payload);
    const refreshed = await dispatch(
      fetchEvergreenConfiguration({ workspaceId: targetWorkspaceId, webinarId: targetWebinarId }),
    );
    if (
      saved &&
      fetchEvergreenConfiguration.fulfilled.match(refreshed) &&
      resolveInteractionsStatus(refreshed.payload) === 'blocked'
    ) {
      toast.warning(t('sessionForm.sources.status.blocked'));
      navigate(`/create-webinar/evergreen/${targetWebinarId}`);
    }
  };

  const nextSessionDate = findNextUpcomingSessionDate(
    currentSessionConfiguration?.sessions,
    new Date(),
  );
  const nextSessionLabel = nextSessionDate
    ? DateTime.fromISO(nextSessionDate, { zone: 'utc' })
        .setZone(workspaceTimezone || DEFAULT_TIMEZONE)
        .setLocale(i18n.language ?? 'fr')
        .toFormat("EEEE d MMMM yyyy - HH:mm'h'")
    : null;

  const saveSessionConfiguration = async (
    targetWorkspaceId: number,
    targetWebinarId: number,
    payload: WebinarSessionConfigurationInput,
  ): Promise<boolean> => {
    const result = await dispatch(
      updateWebinarSessionConfiguration({
        workspaceId: targetWorkspaceId,
        webinarId: targetWebinarId,
        input: payload,
      }),
    );

    if (updateWebinarSessionConfiguration.fulfilled.match(result)) {
      toast.success(t('sessionForm.saveSuccess'));
      hydratedWebinarIdRef.current = null;

      if (workspaceId && webinar?.id) {
        try {
          if (onSessionsChanged) {
            await onSessionsChanged({ realignWithNextSession: true });
          }
          onSuccess?.();
        } catch (error) {
          console.error('Error refreshing webinar after session update:', error);
        }
      } else {
        onSuccess?.();
      }
      return true;
    }
    if (updateWebinarSessionConfiguration.rejected.match(result)) {
      const error = result.payload as AppError | undefined;

      const toastSessionConfigError = (rawMessage: string) => {
        if (rawMessage === 'HTTP.ERROR.400.WEBINAR.NEXT_DATES.DUPLICATES_NOT_ALLOWED') {
          toast.error(t('sessionForm.duplicateSessionsDialog.description'));
          return;
        }
        const mappedMessage = mapHttpError(rawMessage);
        const translatedMessage = tCommon(mappedMessage, rawMessage);
        toast.error(translatedMessage);
      };

      if (error?.details && Array.isArray(error.details) && error.details.length > 0) {
        error.details.forEach((detail: { message?: string }) => {
          if (detail.message) {
            toastSessionConfigError(detail.message);
          }
        });
      } else if (error?.message) {
        toastSessionConfigError(error.message);
      } else {
        toast.error(tCommon('errors.generic', 'Une erreur est survenue lors de la sauvegarde.'));
      }
    }
    return false;
  };

  const handleMp4UploadFinished = (outcome: EvergreenMp4UploadOutcome) => {
    const upload = pendingMp4Upload;
    setPendingMp4Upload(null);
    if (!upload || workspaceId == null) return;
    const refreshed = Promise.all([
      dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId: upload.webinarId })),
      dispatch(fetchWebinarById({ workspaceId, id: upload.webinarId })),
    ]);
    if (outcome === 'failed' || outcome === 'cancelled') {
      toast.warning(t('sessionForm.mp4.videoNotImported'));
      return;
    }
    clearMp4File();
    if (outcome === 'ready') {
      toast.success(t('sessionForm.mp4.videoReady'));
    } else if (outcome === 'pendingPromotion') {
      toast.info(t('sessionForm.mp4.replace.pendingPromotion'));
    } else if (outcome === 'conversionPending') {
      toast.info(t('sessionForm.mp4.conversionPending'));
    } else if (outcome === 'conversionBlocked') {
      void refreshed.then(() => navigate(`/create-webinar/evergreen/${upload.webinarId}`));
    }
  };

  const onSubmitInvalid = (errors: FieldErrors<FormValues>) => {
    const sessionErr = errors.sessions;
    let msg: string | undefined;
    if (sessionErr && typeof sessionErr === 'object') {
      if ('message' in sessionErr && typeof sessionErr.message === 'string') {
        msg = sessionErr.message;
      } else if (
        'root' in sessionErr &&
        sessionErr.root &&
        typeof sessionErr.root === 'object' &&
        'message' in sessionErr.root &&
        typeof sessionErr.root.message === 'string'
      ) {
        msg = sessionErr.root.message;
      }
    }
    if (msg === DUPLICATE_SESSIONS_CODE) {
      toast.error(t('sessionForm.duplicateSessionsDialog.description'));
    }
  };

  const handleDeleteSession = (index: number) => {
    if (sessionFields.length === 1) {
      toast.error(t('sessionForm.deleteDialog.lastSessionError'));
      return;
    }

    const session = form.getValues(`sessions.${index}`) as SessionDateDetails | undefined;
    const sessionIdToDelete = session?.sessionId;

    // Suppression côté API si on a un vrai sessionId mappé depuis le backend
    if (workspaceId && webinar?.id && sessionIdToDelete != null) {
      setDeletingSessionId(sessionIdToDelete);
      void dispatch(
        deleteWebinarSession({
          workspaceId,
          webinarId: webinar.id,
          sessionId: sessionIdToDelete,
        }),
      )
        .unwrap()
        .then(async () => {
          toast.success(t('sessionForm.deleteDialog.success'));
          removeSession(index);
          if (onSessionsChanged) {
            await onSessionsChanged({ excludedSessionId: sessionIdToDelete });
          }
        })
        .catch((error: AppError) => {
          const message = error?.message ? mapHttpError(error.message) : 'errors.generic';
          toast.error(
            tCommon(message, 'Une erreur est survenue lors de la suppression de la session.'),
          );
        })
        .finally(() => {
          setDeletingSessionId(null);
        });
      return;
    }

    // Fallback: session uniquement locale (non encore persistée) -> on la retire juste du formulaire
    removeSession(index);
  };

  const refreshAfterVideoReplacement = () => {
    if (workspaceId == null || activeWebinarId == null) return;
    void dispatch(fetchEvergreenConfiguration({ workspaceId, webinarId: activeWebinarId }));
    void dispatch(fetchWebinarById({ workspaceId, id: activeWebinarId }));
  };

  const pendingImportedMp4FileName =
    currentEvergreenConfiguration?.webinarId === activeWebinarId
      ? (currentEvergreenConfiguration?.pendingImportedMp4FileName ?? null)
      : null;

  const renderInteractionsNotice = () => {
    if (interactionsStatus === 'importing' && savedEvergreenConfiguration) {
      const { conversionProcessedItems, conversionTotalItems } = savedEvergreenConfiguration;
      return (
        <p className="flex items-start gap-2 rounded-lg bg-primary-50 px-3 py-2 text-sm text-tertiary">
          <span className="mt-0.5 shrink-0 text-primary">
            <Loader />
          </span>
          <span>
            {conversionTotalItems > 0
              ? t('sessionForm.sources.status.importingProgress', {
                  processed: Math.min(conversionProcessedItems, conversionTotalItems),
                  total: conversionTotalItems,
                })
              : t('sessionForm.sources.status.importing')}
          </span>
        </p>
      );
    }
    if (interactionsStatus === 'blocked' && activeWebinarId != null) {
      return (
        <div className="flex flex-col gap-2 rounded-lg border border-error/40 bg-error/5 px-3 py-2">
          <p className="text-sm text-error">{t('sessionForm.sources.status.blocked')}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => navigate(`/create-webinar/evergreen/${activeWebinarId}`)}
          >
            {t('sessionForm.mp4.dialog.goToInteractions')}
          </Button>
        </div>
      );
    }
    return null;
  };

  const renderImportedMp4 = (
    videoName: string | null,
    variant: 'replace' | 'importFromLive' = 'replace',
  ) => (
    <ImportedEvergreenVideo
      videoName={videoName}
      variant={variant}
      disabledReason={mp4ReplacementBlockedReason}
      currentDurationSeconds={
        currentEvergreenConfiguration?.webinarId === activeWebinarId
          ? (currentEvergreenConfiguration?.durationSeconds ?? null)
          : null
      }
      pendingFileName={pendingImportedMp4FileName}
      workspaceId={workspaceId ?? null}
      webinarId={activeWebinarId ?? null}
      sessions={currentSessionConfiguration?.sessions}
      timezone={workspaceTimezone || DEFAULT_TIMEZONE}
      maxDurationSeconds={maxSessionDurationMinutes ? maxSessionDurationMinutes * 60 : null}
      onReplaced={refreshAfterVideoReplacement}
    />
  );

  const mp4Picker = (
    <div className="space-y-2">
      <input
        ref={mp4InputRef}
        type="file"
        accept=".mp4,video/mp4"
        onChange={handleMp4InputChange}
        className="hidden"
      />
      {mp4File ? (
        <div className="relative flex items-center gap-2 border border-medium rounded-2xl bg-white p-4 pr-14">
          <div className="size-10 shrink-0 bg-medium rounded-lg flex items-center justify-center">
            <img src="/assets/attach.svg" alt="" />
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <span className="truncate">{mp4File.name}</span>
            <p className="text-sm text-gray-tertiary">
              {formatFileSize(mp4File.size, i18n.language)}
            </p>
          </div>
          <button
            type="button"
            onClick={clearMp4File}
            className="absolute top-1/2 -translate-y-1/2 right-6 cursor-pointer"
            aria-label={t('sessionForm.mp4.removeFile')}
          >
            <Trash2 className="size-6" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(event) => {
            event.preventDefault();
            if (!isCheckingMp4File) setIsDraggingMp4(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setIsDraggingMp4(false);
          }}
          onDrop={handleMp4Drop}
          onClick={() => mp4InputRef.current?.click()}
          className={cn(
            'flex items-center justify-between gap-2 rounded-lg p-4 border-dashed border-2 transition-colors',
            isDraggingMp4 ? 'border-primary bg-primary/5' : 'border-card hover:border-primary/50',
            isCheckingMp4File ? 'opacity-50 pointer-events-none' : 'cursor-pointer',
          )}
        >
          <Label className="text-secondary font-normal">
            {isCheckingMp4File ? t('sessionForm.mp4.checking') : t('sessionForm.dragAndDrop')}
          </Label>
          <Label className="text-secondary font-normal">{t('sessionForm.or')}</Label>
          <Button
            type="button"
            variant="outline"
            className="text-back border-black"
            disabled={isCheckingMp4File}
            onClick={(event) => {
              event.stopPropagation();
              mp4InputRef.current?.click();
            }}
          >
            {t('sessionForm.chooseFile')}
          </Button>
        </div>
      )}
      <p className="text-xs text-secondary">{t('sessionForm.mp4.hint')}</p>
      {mp4FileError && (
        <p className="text-xs text-error">{t(`sessionForm.mp4.errors.${mp4FileError}`)}</p>
      )}
    </div>
  );

  return (
    <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit, onSubmitInvalid)}>
      <div>
        <Controller
          name="type"
          control={form.control}
          render={({ field }) => (
            <RadioGroup
              className="flex items-center gap-4"
              value={field.value}
              onValueChange={field.onChange}
              disabled={!!currentSessionConfiguration}
            >
              {(!currentSessionConfiguration || currentSessionConfiguration.type === 'LIVE') && (
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="LIVE" id="session-live" />
                  <p className="text-base">{t('sessionForm.type.live')}</p>
                </div>
              )}
              {(currentSessionConfiguration?.type === 'EVERGREEN' ||
                !currentSessionConfiguration) && (
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="EVERGREEN" id="session-evergreen" />
                  <p className="text-base">{t('sessionForm.type.evergreen')}</p>
                </div>
              )}
            </RadioGroup>
          )}
        />
      </div>

      {/* Toutes les dates de session */}
      {isSessionsLoading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-secondary">
          <Loader />
          <span className="text-sm">{tCommon('loading')}</span>
        </div>
      ) : (
        <>
          {sessionFields.map((field, index) => {
            const session = form.watch(`sessions.${index}`);
            const dateIsPast = Boolean(
              session?.date &&
              session?.hour &&
              session?.minute &&
              isDatePast(session.date, session.hour, session.minute, session.timezone),
            );
            const isActualNextSession = isRowTheActualNextSession(session);
            const isFinished = session?.status === 'FINISHED';
            const isUpcoming = session?.status === 'UPCOMING';
            const isDisabled = isFinished || dateIsPast || isActualNextSession;
            const canDelete =
              session?.deletable !== false && !isActualNextSession && sessionFields.length > 1;

            const rowContent = (
              <>
                {/* Date */}
                <div className="w-full lg:flex-1 lg:min-w-0">
                  <Controller
                    name={`sessions.${index}.date`}
                    control={form.control}
                    render={({ field: dateField, fieldState }) => {
                      const showError =
                        !!fieldState.error && (fieldState.isTouched || form.formState.isSubmitted);
                      const errorMessage = showError ? fieldState.error?.message : undefined;

                      return (
                        <CalendarInput
                          value={dateField.value}
                          onChange={dateField.onChange}
                          label={t('sessionForm.date')}
                          error={errorMessage}
                          showError={showError}
                          disabledDate={isDisabled}
                        />
                      );
                    }}
                  />
                </div>

                {/* Heure */}
                <div className="w-full lg:flex-1 lg:min-w-0">
                  <Controller
                    name={`sessions.${index}.hour`}
                    control={form.control}
                    render={({ field: hourField }) => (
                      <Select
                        value={hourField.value || '00'}
                        onValueChange={(value) => {
                          hourField.onChange(value || '00');
                          form.trigger(`sessions.${index}.hour`);
                        }}
                        disabled={isDisabled}
                      >
                        <SelectTrigger
                          label={t('sessionForm.time')}
                          className="w-full disabled:text-disable  "
                        >
                          <SelectValue placeholder={t('sessionForm.time')} />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => {
                            const hour = String(i).padStart(2, '0');
                            return (
                              <SelectItem key={hour} value={hour}>
                                {hour}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                {/* Minutes */}
                <div className="w-full lg:flex-1 lg:min-w-0">
                  <Controller
                    name={`sessions.${index}.minute`}
                    control={form.control}
                    render={({ field: minuteField }) => (
                      <Select
                        value={minuteField.value || '00'}
                        onValueChange={(value) => {
                          minuteField.onChange(value || '00');
                          form.trigger(`sessions.${index}.minute`);
                        }}
                        disabled={isDisabled}
                      >
                        <SelectTrigger
                          label={t('sessionForm.minute')}
                          className="w-full disabled:text-disable  "
                        >
                          <SelectValue placeholder={t('sessionForm.time')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="00">00</SelectItem>
                          <SelectItem value="15">15</SelectItem>
                          <SelectItem value="30">30</SelectItem>
                          <SelectItem value="45">45</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="w-full lg:flex-1 lg:min-w-0">
                  <Controller
                    name={`sessions.${index}.timezone`}
                    control={form.control}
                    render={({ field: timezoneField }) => (
                      <Select
                        value={timezoneField.value || workspaceTimezone || DEFAULT_TIMEZONE}
                        onValueChange={timezoneField.onChange}
                        disabled={isDisabled}
                      >
                        <SelectTrigger
                          label={t('sessionForm.timezone')}
                          className="w-full disabled:text-disable"
                        >
                          <SelectValue placeholder={t('sessionForm.timezonePlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Offset DST-aware calculé à la date de CETTE session (ex. Europe/Paris
                              → UTC+2 en été, UTC+1 en hiver). */}
                          {getTimezoneOptions(session?.date).map((tz) => (
                            <SelectItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                {/* Bouton de suppression */}
                <div className="flex items-center gap-2 shrink-0">
                  {!canDelete && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="icon"
                          type="button"
                          className="h-14"
                          disabled={sessionFields.length === 1}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="z-60">
                        <DialogHeader>
                          <DialogTitle>{t('sessionForm.deleteDialog.title')}</DialogTitle>
                          <DialogDescription>
                            {t('sessionForm.deleteDialog.warning')}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="flex flex-col gap-4">
                          <p>{t('sessionForm.deleteDialog.description')}</p>
                        </div>
                        <DialogFooter className="flex justify-between gap-4">
                          <DialogClose asChild>
                            <Button
                              variant="outline"
                              type="button"
                              disabled={deletingSessionId != null}
                            >
                              {t('sessionForm.deleteDialog.cancel')}
                            </Button>
                          </DialogClose>
                          <Button
                            variant="destructive"
                            className="rounded-full"
                            type="button"
                            disabled={deletingSessionId != null}
                            onClick={() => handleDeleteSession(index)}
                          >
                            {deletingSessionId != null &&
                              session?.sessionId === deletingSessionId && <Loader />}
                            {t('sessionForm.deleteDialog.confirm')}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  {canDelete &&
                    (dateIsPast || isFinished ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            <button
                              type="button"
                              onClick={() => removeSession(index)}
                              className="p-2 h-14 shrink-0 cursor-pointer border rounded-lg border-input bg-[#FBFBFB] disabled:cursor-not-allowed disabled:bg-white "
                              aria-label={t('sessionForm.removeDate')}
                              disabled={dateIsPast || isFinished}
                            >
                              <X
                                className={`size-5 ${dateIsPast || isFinished ? 'text-disable' : 'text-red-500'}`}
                              />
                            </button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t('sessionForm.removeDateTooltip')}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeSession(index)}
                        className="p-2 h-14 shrink-0 cursor-pointer border rounded-lg border-input bg-[#FBFBFB] disabled:cursor-not-allowed disabled:bg-white disabled:border-input"
                        aria-label={t('sessionForm.removeDate')}
                        disabled={dateIsPast || isFinished}
                      >
                        <X
                          className={`size-5 ${dateIsPast || isFinished ? 'text-disable' : 'text-red-500'}`}
                        />
                      </button>
                    ))}
                </div>
              </>
            );

            const rowWithTooltip = (tooltipKey: string, extraClassName?: string) => (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'lg:flex w-full space-y-4 lg:space-y-0 items-center gap-4 min-h-0',
                      extraClassName,
                    )}
                  >
                    {rowContent}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t(tooltipKey)}</p>
                </TooltipContent>
              </Tooltip>
            );

            return (
              <div
                key={field.id}
                className="lg:flex w-full space-y-4 lg:space-y-0 items-center gap-4"
              >
                {isFinished
                  ? rowWithTooltip('sessionForm.sessionFinishedTooltip', 'cursor-not-allowed')
                  : isUpcoming
                    ? rowWithTooltip('sessionForm.sessionUpcomingTooltip')
                    : rowContent}
              </div>
            );
          })}

          <button
            type="button"
            className="flex items-center gap-2"
            onClick={() => {
              const currentSessions = form.getValues('sessions');
              const lastSession = currentSessions[currentSessions.length - 1];
              const timezone = lastSession?.timezone || workspaceTimezone || DEFAULT_TIMEZONE;
              const tomorrow = DateTime.now()
                .setZone(timezone)
                .plus({ days: 1 })
                .toFormat('yyyy-MM-dd');
              appendSession({
                date: tomorrow,
                hour: lastSession?.hour || '11',
                minute: lastSession?.minute || '00',
                timezone,
              });
            }}
          >
            <Plus className="size-6" />
            <Label className="cursor-pointer text-sm">{t('sessionForm.addAnotherDate')}</Label>
          </button>
        </>
      )}

      {form.watch('type') === 'EVERGREEN' && (
        <div className="w-full space-y-2">
          <EvergreenSourcesPanel
            selection={sources}
            onChange={(next) => {
              setSources(next);
              setShowSourcesIssue(false);
            }}
            webinars={availableWebinars.filter((candidate) => candidate.id !== webinar?.id)}
            webinarsLoading={availableWebinarsLoading}
            timezone={workspaceTimezone ?? 'utc'}
            locked={sourcesLocked || configurationPending}
            lockedReason={
              configurationPending
                ? t(`sessionForm.sources.configuration.${configurationAvailability}`)
                : sourcesLocked
                  ? t('sessionForm.sources.lockedInProgress')
                  : null
            }
            onReset={
              existingEvergreen && sourcesDirty
                ? () => {
                    setSources(selectionFromConfiguration(existingEvergreen));
                    setShowSourcesIssue(false);
                  }
                : null
            }
            interactionsNotice={renderInteractionsNotice()}
            mp4Content={
              importedMp4VideoName ? (
                renderImportedMp4(importedMp4VideoName)
              ) : savedLiveVideoSessionId != null ? (
                existingEvergreen?.durationSeconds != null ? (
                  renderImportedMp4(null, 'importFromLive')
                ) : (
                  <p className="text-sm text-secondary">
                    {t('sessionForm.mp4.importFromLive.notReady')}
                  </p>
                )
              ) : (
                <div className="space-y-2">
                  {awaitingYoutubeMp4 && (
                    <p className="text-sm text-secondary">
                      {t('sessionForm.mp4.youtubeSourceHint')}
                    </p>
                  )}
                  {mp4Picker}
                </div>
              )
            }
            liveVideoContent={
              liveSourceVideoCard.kind === 'liveRecording' ? renderImportedMp4(null) : null
            }
            videoSessionIdentifier={
              savedEvergreenConfiguration?.videoSourceSession?.identifier ??
              currentSessionConfiguration?.evergreenSession?.sessionIdentifier ??
              null
            }
            interactionsSessionIdentifier={
              savedEvergreenConfiguration?.interactionSourceSession?.identifier ?? null
            }
          />
          {showSourcesIssue && sourcesIssue && (
            <p className="text-xs text-error">{t(`sessionForm.sources.errors.${sourcesIssue}`)}</p>
          )}
          {configurationAvailability === 'UNAVAILABLE' &&
            workspaceId != null &&
            activeWebinarId != null && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  void dispatch(
                    fetchEvergreenConfiguration({ workspaceId, webinarId: activeWebinarId }),
                  )
                }
              >
                {t('sessionForm.sources.configuration.reload')}
              </Button>
            )}
        </div>
      )}
      <div className="w-full">
        <Button
          type="submit"
          variant="default"
          disabled={isSubmitting || isCheckingMp4File}
          className="w-full"
        >
          {isSubmitting && <Loader />}
          {t('sessionForm.saveButton')}
        </Button>
      </div>
      {pendingMp4Upload && workspaceId != null && (
        <EvergreenMp4UploadDialog
          open
          workspaceId={workspaceId}
          webinarId={pendingMp4Upload.webinarId}
          file={pendingMp4Upload.file}
          waitForConversion={pendingMp4Upload.waitForConversion}
          interactionSource={pendingMp4Upload.interactionSource}
          onFinished={handleMp4UploadFinished}
        />
      )}
      {sourcesConfirmation && (
        <EvergreenSourcesChangeDialog
          effect={sourcesConfirmation.effect}
          sourceTitle={sourcesConfirmation.sourceTitle}
          nextSessionLabel={nextSessionLabel}
          onCancel={() => closeSourcesConfirmation(false)}
          onConfirm={() => closeSourcesConfirmation(true)}
        />
      )}
    </form>
  );
}
